/** Draft checked-set helpers for Hallitse signaaleja. Does not rewrite the signal map. */

export function toggle_declared_id(
  checked: ReadonlySet<string>,
  id: string,
  on: boolean,
): Set<string> {
  const next = new Set(checked);
  if (on) next.add(id);
  else next.delete(id);
  return next;
}

export function sorted_declared_ids(
  checked: ReadonlySet<string>,
  catalogueIds: ReadonlySet<string>,
): string[] {
  return [...checked].filter((id) => catalogueIds.has(id)).sort();
}

export function catalogue_ids_from_groups(
  groups: readonly { dataPoints: readonly { id: string }[] }[],
): Set<string> {
  return new Set(
    groups.flatMap((group) => group.dataPoints.map((dp) => dp.id)),
  );
}

function has_taught_lahdesignaali(
  sourceSignalByCanonical: Readonly<Record<string, string>>,
  canonicalId: string,
): boolean {
  return !!sourceSignalByCanonical[canonicalId]?.trim();
}

/**
 * KB catalogue ids that are ready to activate: observed, not yet checked, and
 * already kytketty (taught Lähdesignaali). Observed-without-lähdesignaali is a
 * kytkennät gap, never an aktivointi suggestion.
 */
export function aktivoimatta_catalogue_ids(
  catalogueIds: ReadonlySet<string>,
  observedIds: ReadonlySet<string> | readonly string[],
  checked: ReadonlySet<string>,
  sourceSignalByCanonical: Readonly<Record<string, string>> = {},
): string[] {
  const observed =
    observedIds instanceof Set ? observedIds : new Set(observedIds);
  const out: string[] = [];
  for (const id of catalogueIds) {
    if (!observed.has(id) || checked.has(id)) continue;
    if (!has_taught_lahdesignaali(sourceSignalByCanonical, id)) continue;
    out.push(id);
  }
  return out.sort();
}

/** Lähdesignaali shown on an aktivoimatta row — taught raw name (required for the tag). */
export function aktivoimatta_lahdesignaali_label(
  sourceSignalByCanonical: Readonly<Record<string, string>>,
  canonicalId: string,
): string {
  return sourceSignalByCanonical[canonicalId]?.trim() || "ei lähdesignaalia";
}

/** Check every observed+kytketty catalogue id that is still off. */
export function activate_observed_catalogue_ids(
  checked: ReadonlySet<string>,
  catalogueIds: ReadonlySet<string>,
  observedIds: ReadonlySet<string> | readonly string[],
  sourceSignalByCanonical: Readonly<Record<string, string>> = {},
): Set<string> {
  const next = new Set(checked);
  for (const id of aktivoimatta_catalogue_ids(
    catalogueIds,
    observedIds,
    checked,
    sourceSignalByCanonical,
  )) {
    if (catalogueIds.has(id)) next.add(id);
  }
  return next;
}

type FlatDp = { id: string; name_fi?: string; groupLabel: string };

/** Flat catalogue rows with aktivoimatta (observed+kytketty+unchecked) first. */
export function flatten_activation_rows(
  groups: readonly {
    label?: string | null;
    dataPoints: readonly { id: string; name_fi?: string }[];
  }[],
  observedIds: ReadonlySet<string> | readonly string[],
  checked: ReadonlySet<string>,
  sourceSignalByCanonical: Readonly<Record<string, string>> = {},
): FlatDp[] {
  const pending = new Set(
    aktivoimatta_catalogue_ids(
      catalogue_ids_from_groups(groups),
      observedIds,
      checked,
      sourceSignalByCanonical,
    ),
  );
  const rows: FlatDp[] = groups.flatMap((group) =>
    group.dataPoints.map((dp) => ({
      id: dp.id,
      name_fi: dp.name_fi,
      groupLabel: group.label ?? "",
    })),
  );
  return rows.sort((a, b) => {
    const aSug = pending.has(a.id) ? 0 : 1;
    const bSug = pending.has(b.id) ? 0 : 1;
    if (aSug !== bSug) return aSug - bSug;
    return a.id.localeCompare(b.id, "fi");
  });
}
